import type { VoidFunctionComponent } from 'react';
import Card from '../../generic/Card/Card';
import Text from '../../generic/Text';
import { useOnConfirm } from '../../../contexts/DialogContext';
import Button from '../../generic/Button';
import useDeleteAllProjects from './useDeleteAllProjects';
import { useAddToast } from '../../../contexts/ToastContext';

const over1000ProjectsToastText = 'Projects are queued for deletion across the next 7 days. Check back next week.';
const over100ProjectsToastText = 'Projects are queued for deletion across the next 24 hours. Check back tomorrow.';
const fewProjectsToastText = 'Projects are queued for deletion. Check back soon.';

function getOverLimitMessage(numProjects: number): string {
  if (numProjects > 1000) {
    return over1000ProjectsToastText;
  }

  if (numProjects > 100) {
    return over100ProjectsToastText;
  }

  return fewProjectsToastText;
}

const DeleteAllProjectsCard: VoidFunctionComponent = () => {
  const onConfirm = useOnConfirm();
  const addToast = useAddToast();
  const { data: numProjectsQueued, mutate: deleteAllProjects } = useDeleteAllProjects();

  return (
    <Card isFullWidth>
      <Card.Header>Delete All Projects</Card.Header>
      <Card.Content>
        <div>
          Delete all of the projects in the organization in background jobs. If there are less than 100 projects, they
          will all be queued immediately. If there are more than 100 projects but fewer than 1000, they
          will be spread out across the next 24 hours. If there are more than 1000, they
          will be spread out across the next 7 days.
        </div>
        <Text.H3>
          Only do this if the org is going to be deleted entirely.
        </Text.H3>

        <Button onClick={() => onConfirm({
          title: 'Delete All Projects',
          body: (
            <div>
              Are you sure you want to delete all projects in this organization?
            </div>
          ),
          onConfirm: () => {
            deleteAllProjects();

            const message = getOverLimitMessage(numProjectsQueued?.data?.count ?? 0);

            addToast({
              type: 'success',
              content: message,
            });
          },
        })}
        >
          Delete All Projects
        </Button>
      </Card.Content>
    </Card>
  );
};

export default DeleteAllProjectsCard;
